import { useEffect, useState, type ReactNode } from "react";
import { Boxes, MapPin } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { formatDate, formatDateTime } from "../../lib/utils";
import {
  getDigitalTwin,
  updateDigitalTwinLocation,
  type DigitalTwin,
} from "../../lib/digitalTwinsApi";

interface DigitalTwinDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  digitalTwinId: string | null;
  /** When true, the Location section becomes editable (admins only). */
  canEdit?: boolean;
  /** Called after coordinates are saved so the parent map/list can refresh. */
  onUpdated?: () => void;
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const mb = bytes / (1024 * 1024);
  return mb > 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-surface-700/40 last:border-0">
      <dt className="text-sm text-surface-400 shrink-0">{label}</dt>
      <dd className="text-sm text-surface-100 text-right break-all max-w-[60%]">
        {children}
      </dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-2">
        {title}
      </h3>
      <dl className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-4 py-1">
        {children}
      </dl>
    </div>
  );
}

export function DigitalTwinDetailsModal({
  isOpen,
  onClose,
  digitalTwinId,
  canEdit = false,
  onUpdated,
}: DigitalTwinDetailsModalProps) {
  const [twin, setTwin] = useState<DigitalTwin | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !digitalTwinId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setSaveError(null);
    setTwin(null);
    getDigitalTwin(digitalTwinId)
      .then((res) => {
        if (cancelled) return;
        setTwin(res);
        setLatInput(res.latitude != null ? String(res.latitude) : "");
        setLngInput(res.longitude != null ? String(res.longitude) : "");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { response?: { data?: { detail?: string } } };
        setError(e.response?.data?.detail || "Failed to load digital twin details.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, digitalTwinId]);

  async function handleSaveLocation() {
    if (!digitalTwinId) return;
    const lat = Number(latInput.trim());
    const lng = Number(lngInput.trim());
    if (!latInput.trim() || !lngInput.trim() || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSaveError("Enter numeric latitude and longitude.");
      return;
    }
    if (lat < -90 || lat > 90) {
      setSaveError("Latitude must be between −90 and 90.");
      return;
    }
    if (lng < -180 || lng > 180) {
      setSaveError("Longitude must be between −180 and 180.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateDigitalTwinLocation(digitalTwinId, {
        latitude: lat,
        longitude: lng,
      });
      setTwin(updated);
      onUpdated?.();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setSaveError(e.response?.data?.detail || "Failed to save location.");
    } finally {
      setSaving(false);
    }
  }

  const locationDirty =
    !!twin &&
    (latInput.trim() !== (twin.latitude != null ? String(twin.latitude) : "") ||
      lngInput.trim() !== (twin.longitude != null ? String(twin.longitude) : ""));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Digital Twin Details" size="xl">
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" label="Loading details..." />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {twin && !isLoading && (
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/15">
              <Boxes className="h-5 w-5 text-purple-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-surface-100 truncate">{twin.name}</h2>
              {twin.description && (
                <p className="text-sm text-surface-400 mt-0.5">{twin.description}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Identity">
              <Row label="ID">
                <code className="font-mono text-xs">{twin.id}</code>
              </Row>
              <Row label="Virtual Site">
                <code className="font-mono text-xs">{twin.virtual_site_id}</code>
              </Row>
              <Row label="Status">
                <Badge
                  variant={
                    twin.status === "ready"
                      ? "success"
                      : twin.status === "error"
                        ? "danger"
                        : "warning"
                  }
                >
                  {twin.status}
                </Badge>
              </Row>
              <Row label="Capture Date">
                {twin.capture_date ? formatDate(twin.capture_date) : "—"}
              </Row>
              <Row label="Coordinate System">
                {twin.coordinate_system || "—"}
              </Row>
              <Row label="Point Count">
                {twin.point_count != null ? twin.point_count.toLocaleString() : "—"}
              </Row>
            </Section>

            <Section title="Format & Size">
              <Row label="Source Format">
                <Badge variant="brand">.{twin.format}</Badge>
              </Row>
              <Row label="Source Size">{formatBytes(twin.file_size_bytes)}</Row>
              <Row label="Derived Format">
                {twin.derived_format ? (
                  <Badge variant="success">.{twin.derived_format}</Badge>
                ) : (
                  "—"
                )}
              </Row>
              <Row label="Derived Size">{formatBytes(twin.derived_file_size_bytes)}</Row>
            </Section>

            <Section title="Storage">
              <Row label="Bucket">{twin.s3_bucket}</Row>
              <Row label="Region">{twin.s3_region}</Row>
              <Row label="Source Key">
                <code className="font-mono text-xs">{twin.s3_key}</code>
              </Row>
              <Row label="Derived Key">
                {twin.derived_s3_key ? (
                  <code className="font-mono text-xs">{twin.derived_s3_key}</code>
                ) : (
                  "—"
                )}
              </Row>
            </Section>

            <Section title="Audit">
              <Row label="Created At">{formatDateTime(twin.created_at)}</Row>
              <Row label="Updated At">{formatDateTime(twin.updated_at)}</Row>
              <Row label="Created By">
                <span className="text-surface-500 italic">not tracked</span>
              </Row>
              <Row label="Updated By">
                <span className="text-surface-500 italic">not tracked</span>
              </Row>
            </Section>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-surface-500 mb-2">
              Location
            </h3>
            <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-4 py-3">
              {canEdit ? (
                <div className="space-y-3">
                  <p className="flex items-center gap-1.5 text-xs text-surface-400">
                    <MapPin className="h-3.5 w-3.5" />
                    WGS84 decimal degrees — sets where this twin appears on the 2D map.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Latitude"
                      type="number"
                      step="any"
                      inputMode="decimal"
                      placeholder="e.g., 51.5074"
                      value={latInput}
                      onChange={(e) => setLatInput(e.target.value)}
                      disabled={saving}
                    />
                    <Input
                      label="Longitude"
                      type="number"
                      step="any"
                      inputMode="decimal"
                      placeholder="e.g., -0.1278"
                      value={lngInput}
                      onChange={(e) => setLngInput(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  {saveError && <p className="text-xs text-red-400">{saveError}</p>}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={handleSaveLocation}
                      isLoading={saving}
                      disabled={!locationDirty}
                    >
                      Save location
                    </Button>
                  </div>
                </div>
              ) : (
                <dl>
                  <Row label="Latitude">
                    {twin.latitude != null ? twin.latitude : "—"}
                  </Row>
                  <Row label="Longitude">
                    {twin.longitude != null ? twin.longitude : "—"}
                  </Row>
                </dl>
              )}
            </div>
          </div>

          {twin.conversion_metadata && (
            <Section title="Conversion">
              {twin.conversion_metadata.converter !== undefined && (
                <Row label="Converter">
                  {String(twin.conversion_metadata.converter)}
                  {twin.conversion_metadata.converter_version
                    ? ` (${twin.conversion_metadata.converter_version})`
                    : ""}
                </Row>
              )}
              {twin.conversion_metadata.spz_version !== undefined && (
                <Row label="SPZ Version">{String(twin.conversion_metadata.spz_version)}</Row>
              )}
              {twin.conversion_metadata.started_at && (
                <Row label="Started">{formatDateTime(twin.conversion_metadata.started_at)}</Row>
              )}
              {twin.conversion_metadata.finished_at && (
                <Row label="Finished">{formatDateTime(twin.conversion_metadata.finished_at)}</Row>
              )}
              {twin.conversion_metadata.duration_seconds != null && (
                <Row label="Duration">
                  {twin.conversion_metadata.duration_seconds.toFixed(1)}s
                </Row>
              )}
              {twin.conversion_metadata.skipped_reason && (
                <Row label="Skipped">{twin.conversion_metadata.skipped_reason}</Row>
              )}
              {twin.conversion_metadata.error_message && (
                <Row label="Error">
                  <span className="text-red-300">{twin.conversion_metadata.error_message}</span>
                </Row>
              )}
            </Section>
          )}

          {twin.bounds && Object.keys(twin.bounds).length > 0 && (
            <Section title="Bounds">
              {Object.entries(twin.bounds).map(([key, value]) => (
                <Row key={key} label={key}>
                  {typeof value === "number" ? value.toFixed(3) : String(value)}
                </Row>
              ))}
            </Section>
          )}

          {twin.metadata_ && Object.keys(twin.metadata_).length > 0 && (
            <Section title="Metadata">
              {Object.entries(twin.metadata_).map(([key, value]) => (
                <Row key={key} label={key}>
                  {typeof value === "object" ? JSON.stringify(value) : String(value)}
                </Row>
              ))}
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}
